/**
 * Builds the combined sort options list: standard SORT_OPTIONS + custom metrics.
 *
 * Merges the hardcoded sort fields with dynamic custom metric sorts from the
 * backend, deduplicating by field key. Used by every player-search surface
 * that offers a sort control.
 * @module hooks/useAllSortOptions
 */

import { useMemo } from "react";

import { SORT_OPTIONS } from "@/features/scouting/constants/sortOptions";
import type { SortOption } from "@/features/scouting/types/scouting-target";
import { useDynamicSortOptions } from "@/hooks/useDynamicSortOptions";

/**
 * Returns the full list of sort options including custom metrics.
 * @param options - Forwarded to {@link useDynamicSortOptions}; pass
 *   `enabled: false` while the sort surface is closed (SE-8832)
 * @param options.enabled - Whether the caller needs the custom metrics now
 * @returns Combined readonly array of standard + custom sort options
 */
export const useAllSortOptions = (options?: {
  readonly enabled?: boolean;
}): readonly SortOption[] => {
  const { enhancedMenuSelectionSortList } = useDynamicSortOptions(options);

  return useMemo(
    () => [
      ...SORT_OPTIONS,
      ...enhancedMenuSelectionSortList
        .filter(
          option => !SORT_OPTIONS.some(so => so.field === String(option.type))
        )
        .map(option => ({
          field: String(option.type),
          label: option.title,
        })),
    ],
    [enhancedMenuSelectionSortList]
  );
};
